import { useTranslate } from 'react-admin';
import { Autocomplete, Chip, Box, TextField, Checkbox } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import type { TicketQueueOption } from '../types/tickets.types';

interface TicketsQueueSelectProps {
  selectedQueueIds: number[];
  userQueues: TicketQueueOption[];
  onChange: (values: number[]) => void;
  style?: React.CSSProperties;
}

/**
 * TicketsQueueSelect - Autocomplete multi-select for filtering tickets by queue
 *
 * Compact when closed (shows selected queue chips or "All" placeholder).
 * Opens a searchable dropdown on click. Works well on all screen sizes.
 */
export function TicketsQueueSelect({
  selectedQueueIds,
  userQueues = [],
  onChange,
  style,
}: TicketsQueueSelectProps) {
  const translate = useTranslate();

  const selectedQueues = userQueues.filter((q) =>
    selectedQueueIds.includes(q.id),
  );

  if (userQueues.length <= 1) {
    return null;
  }

  return (
    <Box
      style={style}
      sx={{
        px: 1,
        py: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
        flexShrink: 0,
      }}
    >
      <Autocomplete
        multiple
        size="small"
        disableCloseOnSelect
        options={userQueues}
        value={selectedQueues}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_event, newValue) => {
          onChange(newValue.map((q) => q.id));
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...rest } = props;
          return (
            <li key={key} {...rest}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                checked={selected}
                size="small"
                sx={{ mr: 0.5, p: 0.25 }}
              />
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: option.color || '#7C7C7C',
                  mr: 1,
                  flexShrink: 0,
                }}
              />
              {option.name}
            </li>
          );
        }}
        renderTags={(value, getTagProps) =>
          value.map((queue, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                {...tagProps}
                label={queue.name}
                size="small"
                icon={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: queue.color || '#7C7C7C',
                      flexShrink: 0,
                    }}
                  />
                }
                sx={{
                  height: 20,
                  fontSize: 11,
                  maxWidth: 100,
                  '& .MuiChip-label': { px: 0.5 },
                  '& .MuiChip-icon': { ml: 0.5, mr: -0.25, minWidth: 8 },
                  '& .MuiChip-deleteIcon': { fontSize: 14 },
                }}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              selectedQueueIds.length === 0
                ? translate('resources.tickets.queueSelect.allQueues', { _: 'All Queues' })
                : undefined
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                py: '2px !important',
                minHeight: 32,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'divider',
              },
            }}
          />
        )}
        slotProps={{
          paper: { sx: { maxHeight: 240 } },
          listbox: { sx: { py: 0.5 } },
        }}
        sx={{
          '& .MuiAutocomplete-tag': {
            my: 0,
          },
        }}
      />
    </Box>
  );
}

export default TicketsQueueSelect;
